import { createTheme, ThemeProvider, CircularProgress, Box } from "@mui/material";
import { useEffect } from "react";
import {
    ClientPortalConfigContextProvider,
    useClientPortalConfigContext,
    AuthorizationContextProvider,
    ResetPasswordContextProvider,
} from "./context";
import { useClientPortalTheme } from "hooks/useClientPortalTheme";
import { useRumPageTracking } from "hooks/useRumPageTracking";
import { initAnalytics } from "utils/analytics";
import { ConfirmProvider } from "material-ui-confirm";
import { SnackbarProvider } from "@powerdiary/components-library/lib";
import Layout from "./components/Layout";
import SomethingWentWrong from "./components/ErrorPages/SomethingWentWrong";

interface IClientPortalProps {
    clientPortalGuid: string;
}

function ClientPortalInner() {
    const { isLoading, clientPortalConfig } = useClientPortalConfigContext();
    const { clientPortalThemeOptions } = useClientPortalTheme();

    useEffect(() => {
        const analyticsId = clientPortalConfig?.googleAnalyticsId;
        if (!analyticsId) return;

        if (clientPortalConfig?.googleAnalyticsEnabled) {
            initAnalytics(analyticsId, { sendPageView: true });
        }
    }, [clientPortalConfig]);

    if (isLoading) {
        return (
            <Box display="flex" alignItems="center" minWidth="100vw" minHeight="100vh" justifyContent="center">
                <CircularProgress color="inherit" />
            </Box>
        );
    }

    if (!clientPortalConfig) {
        return <SomethingWentWrong />;
    }

    return (
        <ThemeProvider theme={createTheme(clientPortalThemeOptions)}>
            <SnackbarProvider autoHideDuration={10000} preventDuplicate>
                <AuthorizationContextProvider>
                    <ResetPasswordContextProvider>
                        <ConfirmProvider>
                            <Layout />
                        </ConfirmProvider>
                    </ResetPasswordContextProvider>
                </AuthorizationContextProvider>
            </SnackbarProvider>
        </ThemeProvider>
    );
}

export default function ClientPortal({ clientPortalGuid }: Readonly<IClientPortalProps>) {
    useRumPageTracking(clientPortalGuid);

    return (
        <ClientPortalConfigContextProvider clientPortalGuid={clientPortalGuid}>
            <ClientPortalInner />
        </ClientPortalConfigContextProvider>
    );
}
